import { arrayMove } from '@dnd-kit/sortable'

export type FlowContainerId = 'unordered' | 'ordered'

export interface FlowDragContainers {
  unordered: string[]
  ordered: string[]
}

export interface FlowRanking {
  r: number
  n?: string
  notAvailable: boolean
}

const DEFAULT_ORDER_GAP = 100

export function getContainerForId(
  containers: FlowDragContainers,
  id: string
): FlowContainerId | null {
  if (id === 'unordered' || id === 'ordered') return id
  if (containers.unordered.includes(id)) return 'unordered'
  if (containers.ordered.includes(id)) return 'ordered'
  return null
}

export function arraysEqual(a: string[], b: string[]): boolean {
  if (a === b) return true
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

export interface MoveFlowItemParams {
  containers: FlowDragContainers
  activeId: string
  overId: string
  /**
   * Current domain constraint: items can be added to ordered, but not removed
   * (no command supports deleting ranking entries).
   */
  allowMoveFromOrderedToUnordered?: boolean
}

export function moveFlowItemBetweenContainers(
  params: MoveFlowItemParams
): FlowDragContainers {
  const {
    containers,
    activeId,
    overId,
    allowMoveFromOrderedToUnordered = false,
  } = params

  const activeContainer = getContainerForId(containers, activeId)
  const overContainer = getContainerForId(containers, overId)

  if (!activeContainer || !overContainer) return containers

  // Reorder within a container
  if (activeContainer === overContainer) {
    const items = containers[activeContainer]

    // If hovering over the container itself, don't reorder
    if (overId === activeContainer) return containers

    const oldIndex = items.indexOf(activeId)
    const newIndex = items.indexOf(overId)

    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
      return containers
    }

    const moved = arrayMove(items, oldIndex, newIndex)
    if (arraysEqual(moved, items)) return containers

    return { ...containers, [activeContainer]: moved }
  }

  // Cross-container move
  if (!allowMoveFromOrderedToUnordered && activeContainer === 'ordered' && overContainer === 'unordered') {
    return containers
  }

  const fromItems = containers[activeContainer]
  const toItems = containers[overContainer]

  const fromIndex = fromItems.indexOf(activeId)
  if (fromIndex === -1) return containers

  const nextFrom = [...fromItems]
  nextFrom.splice(fromIndex, 1)

  const nextTo = [...toItems]

  if (overId === overContainer) {
    // Dropped on container: append
    nextTo.push(activeId)
  } else {
    const toIndex = toItems.indexOf(overId)
    if (toIndex === -1) {
      nextTo.push(activeId)
    } else {
      nextTo.splice(toIndex, 0, activeId)
    }
  }

  if (arraysEqual(nextFrom, fromItems) && arraysEqual(nextTo, toItems)) {
    return containers
  }

  return {
    ...containers,
    [activeContainer]: nextFrom,
    [overContainer]: nextTo,
  }
}

export interface ComputeFlowRankPatchParams {
  rankings: Record<string, FlowRanking>
  orderedIdsAfter: string[]
  activeId: string
  itemName?: string
  orderGap?: number
}

export interface ComputeFlowAppendManyPatchParams {
  rankings: Record<string, FlowRanking>
  orderedIds: string[]
  idsToAppend: string[]
  itemNames?: Record<string, string>
  orderGap?: number
}

export function computeFlowRankingsPatchForOrderedPosition(
  params: ComputeFlowRankPatchParams
): Record<string, FlowRanking> | null {
  const { rankings, orderedIdsAfter, activeId, itemName, orderGap = DEFAULT_ORDER_GAP } = params

  const newIndex = orderedIdsAfter.indexOf(activeId)
  if (newIndex === -1) return null

  let newRank: number

  if (orderedIdsAfter.length === 1) {
    newRank = orderGap
  } else if (newIndex === 0) {
    const nextId = orderedIdsAfter[1]
    const nextRank = rankings[nextId]?.r ?? orderGap
    newRank = nextRank > 0 ? nextRank / 2 : nextRank - orderGap
  } else if (newIndex === orderedIdsAfter.length - 1) {
    const prevId = orderedIdsAfter[newIndex - 1]
    const prevRank = rankings[prevId]?.r ?? 0
    newRank = prevRank + orderGap
  } else {
    const prevId = orderedIdsAfter[newIndex - 1]
    const nextId = orderedIdsAfter[newIndex + 1]
    const prevRank = rankings[prevId]?.r ?? 0
    const nextRank = rankings[nextId]?.r ?? (prevRank + orderGap * 2)
    newRank = (prevRank + nextRank) / 2
  }

  return {
    [activeId]: { r: newRank, notAvailable: false, n: itemName },
  }
}

export function computeFlowRankingsPatchForAppendMany(
  params: ComputeFlowAppendManyPatchParams
): Record<string, FlowRanking> {
  const { rankings, orderedIds, idsToAppend, itemNames, orderGap = DEFAULT_ORDER_GAP } = params

  if (idsToAppend.length === 0) return {}

  const lastOrderedId = orderedIds.length > 0 ? orderedIds[orderedIds.length - 1] : undefined
  const lastRank = lastOrderedId ? (rankings[lastOrderedId]?.r ?? 0) : 0

  const patch: Record<string, FlowRanking> = {}
  let rank = lastRank

  for (const id of idsToAppend) {
    // If item already has a ranking (shouldn't happen for unordered selection), skip.
    if (rankings[id]) continue
    rank += orderGap
    const n = itemNames ? itemNames[id] : undefined
    patch[id] = { r: rank, notAvailable: false, n }
  }

  return patch
}

export interface ComputeFlowPopupOrderPatchParams {
  popupItemIds: string[]
  prevRank: number | null
  nextRank: number | null
  itemNames?: Record<string, string>
  existingRankings?: Record<string, FlowRanking>
  orderGap?: number
}

export function computeFlowRankingsPatchForPopupOrder(
  params: ComputeFlowPopupOrderPatchParams
): Record<string, FlowRanking> {
  const { popupItemIds, prevRank, nextRank, itemNames, existingRankings = {}, orderGap = DEFAULT_ORDER_GAP } = params

  if (popupItemIds.length === 0) return {}

  const count = popupItemIds.length
  let startRank: number
  let step: number

  if (prevRank !== null && nextRank !== null) {
    step = (nextRank - prevRank) / (count + 1)
    startRank = prevRank + step
  } else if (prevRank !== null) {
    step = orderGap
    startRank = prevRank + orderGap
  } else if (nextRank !== null) {
    step = nextRank / (count + 1)
    startRank = step
  } else {
    step = orderGap
    startRank = orderGap
  }

  const patch: Record<string, FlowRanking> = {}
  for (let i = 0; i < count; i++) {
    const id = popupItemIds[i]
    const existing = existingRankings[id]
    patch[id] = {
      r: startRank + i * step,
      notAvailable: existing?.notAvailable ?? false,
      n: itemNames?.[id] ?? existing?.n,
    }
  }

  return patch
}

export function computeFlowRankingsNamePatch(params: {
  rankings: Record<string, { r: number; n?: string; notAvailable: boolean }>
  itemId: string
  itemName: string
}): Record<string, { r: number; n?: string; notAvailable: boolean }> | null {
  const { rankings, itemId, itemName } = params

  const existing = rankings[itemId]
  if (!existing) return null

  const trimmed = String(itemName ?? '').trim()
  if (!trimmed) return null

  if (existing.n === trimmed) return null

  return {
    [itemId]: {
      ...existing,
      n: trimmed,
    },
  }
}
